import {
  QButton,
  QCheckbox,
  QFormControl,
  QModal,
  QModalActions,
  QModalBody,
  QModalHeader,
  QRadio,
  QRadioGroup,
  QStack,
  QText,
  useCurrentUser,
  useFeatureFlag,
} from '@qualio/ui-components';
import { ChangeEvent, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DocumentFormat,
  QualioDocument,
} from '../../../../../../../api/model/document';
import {
  isBasicUserInAccount,
  isGhostedUser,
  isQualityUserInAccount,
} from '../../../../../../../util/CurrentUser';

interface BulkExportModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedDocuments: QualioDocument[];
  onExport: (
    coverPageEnabled: boolean,
    controlledStatus: 'controlled' | 'uncontrolled',
    shouldShowShrinkingOption: boolean,
    contentShrinkingEnabled: boolean,
    includeChangeControl: boolean,
    includeDocx: boolean,
  ) => void;
}

export const BulkExportModal = ({
  isOpen,
  onClose,
  selectedDocuments,
  onExport,
}: BulkExportModalProps) => {
  const { t } = useTranslation();
  const currentUser = useCurrentUser();
  const [controlledStatus, setControlledStatus] = useState<
    'controlled' | 'uncontrolled'
  >('uncontrolled');
  const [coverPageEnabled, setCoverPageEnabled] = useState(true);
  const [contentShrinkingEnabled, setContentShrinkingEnabled] = useState(true);
  const [includeChangeControl, setIncludeChangeControl] = useState(false);
  const [includeDocx, setIncludeDocx] = useState(false);
  const isQualityUser = isQualityUserInAccount(currentUser);
  const isBasicUser = isBasicUserInAccount(currentUser);
  const isGhosted = isGhostedUser(currentUser);
  const canSelectControlled = isQualityUser && !isGhosted;
  const featureFlags = useFeatureFlag();

  const onControlledCopyChange = useCallback(
    (value: 'controlled' | 'uncontrolled') => {
      setControlledStatus(value);
    },
    [],
  );

  const onCoverPageChange = useCallback(
    (evt: ChangeEvent<HTMLInputElement>) => {
      setCoverPageEnabled(evt.target.checked);
    },
    [],
  );

  const onContentShrinkingChange = useCallback(
    (evt: ChangeEvent<HTMLInputElement>) => {
      setContentShrinkingEnabled(evt.target.checked);
    },
    [],
  );

  const onIncludeChangeControlChange = useCallback(
    (evt: ChangeEvent<HTMLInputElement>) => {
      setIncludeChangeControl(evt.target.checked);
    },
    [],
  );

  const onIncludeDocxChange = useCallback(
    (evt: ChangeEvent<HTMLInputElement>) => {
      setIncludeDocx(evt.target.checked);
    },
    [],
  );

  const onCloseCallback = useCallback(() => {
    setCoverPageEnabled(true);
    setIncludeChangeControl(false);
    setIncludeDocx(false);
    onClose();
  }, [onClose]);

  const shouldShowCoverPageOptions = useMemo(
    () => selectedDocuments.some((doc) => doc.editor_version !== null),
    [selectedDocuments],
  );

  const shouldShowTypeOptions = useMemo(
    () =>
      selectedDocuments.some(
        (doc) => doc.status_id === 'effective' || doc.status_id === 'approved',
      ),
    [selectedDocuments],
  );

  const shouldShowShrinkingOption = useMemo(
    () =>
      selectedDocuments.some(
        (doc) => doc.document_format === DocumentFormat.DocumentUpload,
      ),
    [selectedDocuments],
  );

  const handleExportDocuments = useCallback(() => {
    onExport(
      coverPageEnabled,
      controlledStatus,
      shouldShowShrinkingOption,
      contentShrinkingEnabled,
      includeChangeControl,
      includeDocx,
    );
    onCloseCallback();
  }, [
    onExport,
    coverPageEnabled,
    controlledStatus,
    shouldShowShrinkingOption,
    contentShrinkingEnabled,
    includeChangeControl,
    includeDocx,
    onCloseCallback,
  ]);

  const controlledStatusValue = canSelectControlled
    ? controlledStatus
    : 'uncontrolled';

  return (
    <QModal onClose={onCloseCallback} isOpen={isOpen} size="xl">
      <QModalHeader>
        <QText>{t('modals.bulkExportDocuments')}</QText>
      </QModalHeader>
      <QModalBody>
        <QStack spacing={6}>
          <>
            {shouldShowTypeOptions && (
              <QFormControl
                label={t('labels.type')}
                helper={t('forms.controlledStatusOnlyAppliedToApproved')}
              >
                <QRadioGroup
                  value={controlledStatusValue}
                  onChange={onControlledCopyChange}
                  data-cy="export-modal-type-selection"
                >
                  <QStack direction="row">
                    <QRadio
                      value="uncontrolled"
                      data-cy="export-modal-uncontrolled-type"
                    >
                      {t('labels.type').replace('Type', 'Uncontrolled')}
                    </QRadio>
                    <QRadio
                      isDisabled={!canSelectControlled}
                      value="controlled"
                      data-cy="export-modal-controlled-type"
                    >
                      {t('labels.type').replace('Type', 'Controlled')}
                    </QRadio>
                  </QStack>
                </QRadioGroup>
              </QFormControl>
            )}

            {shouldShowCoverPageOptions && (
              <QStack spacing={0}>
                <QFormControl
                  label={t('forms.coverPage')}
                  helper={t('labels.none')}
                >
                  <QCheckbox
                    isChecked={coverPageEnabled}
                    onChange={onCoverPageChange}
                    data-cy="export-modal-coverpage-selection"
                  >
                    {t('forms.coverPage').replace(
                      'Cover page',
                      'Include cover page',
                    )}
                  </QCheckbox>
                </QFormControl>
              </QStack>
            )}

            {featureFlags['replace-export-ui'] && (
              <QStack spacing={0}>
                <QFormControl
                  label={t('forms.includeChangeControl')}
                  helper={t('forms.includesChangeControlInformation')}
                >
                  <QCheckbox
                    isChecked={includeChangeControl}
                    onChange={onIncludeChangeControlChange}
                    data-cy="export-modal-include-change-control-selection"
                  >
                    {t('forms.includeChangeControl')}
                  </QCheckbox>
                </QFormControl>
              </QStack>
            )}

            {!isBasicUser &&
              selectedDocuments.every(
                (doc) =>
                  doc.document_format === DocumentFormat.QualioNativeDocument,
              ) && (
                <QStack spacing={0}>
                  <QFormControl
                    label={t('forms.includeDocx')}
                    helper={t('forms.includesDocxInformation')}
                  >
                    <QCheckbox
                      isChecked={includeDocx}
                      onChange={onIncludeDocxChange}
                      data-cy="export-modal-include-docx-selection"
                    >
                      {t('forms.includeDocx')}
                    </QCheckbox>
                  </QFormControl>
                </QStack>
              )}

            {shouldShowShrinkingOption && (
              <QStack spacing={0}>
                <QFormControl
                  label={t('forms.contentShrinking')}
                  helper={t('forms.automaticallyShrinkContentForHeaders')}
                >
                  <QCheckbox
                    isChecked={contentShrinkingEnabled}
                    onChange={onContentShrinkingChange}
                    data-cy="export-modal-content-shrinking-selection"
                  >
                    {t('forms.contentShrinking').replace(
                      'Content shrinking',
                      'Enable content shrinking',
                    )}
                  </QCheckbox>
                </QFormControl>
              </QStack>
            )}
          </>
        </QStack>
      </QModalBody>
      <QModalActions>
        <QButton variant="outline" onClick={onCloseCallback}>
          {t('buttons.cancel')}
        </QButton>
        <QButton
          onClick={handleExportDocuments}
          data-cy="export-document-button"
        >
          {t('buttons.exportDocument').replace(
            'Export document',
            'Export documents',
          )}
        </QButton>
      </QModalActions>
    </QModal>
  );
};
